import { get, isNil } from 'lodash';
import { useMemo } from 'react';
import useNgpFormContext from '../../../utilities/NgpFormContextProviderUtilities';

export const EMPTY_VALUE = '';
export const EMPTY_IP_VALUE = '--';

/**
 * Safely extracts field value from form data with default fallback
 * @param {Object} formData - Form data object
 * @param {string} fieldPath - Field path to extract
 * @param {*} defaultValue - Default value if field is missing
 * @returns {*} Field value or default
 */
export const getFieldValue = (formData, fieldPath, defaultValue = EMPTY_VALUE) => {
    if (isNil(formData) || isNil(fieldPath)) {
        return defaultValue;
    }
    return get(formData, fieldPath, defaultValue);
};

/**
 * Safely formats value using a formatter function
 * @param {Function} formatter - Formatter function
 * @param {*} value - Value to format
 * @param {*} defaultValue - Default value if formatting fails
 * @returns {*} Formatted value or default
 */
export const safeFormat = (formatter, value, defaultValue = EMPTY_VALUE) => {
    if (isNil(formatter) || isNil(value)) {
        return defaultValue;
    }
    try {
        return formatter(value) ?? defaultValue;
    } catch (error) {
        return defaultValue;
    }
};

/**
 * Custom hook to extract and memoize property values from form data
 * @param {Function} extractorFn - Function that extracts property values from formData
 * @returns {Object} Memoized property values
 */
export const usePropertyValues = (extractorFn) => {
    const { formData } = useNgpFormContext();

    const propertyValues = useMemo(() => {
        if (isNil(formData) || isNil(extractorFn)) {
            return {};
        }
        return extractorFn(formData);
    }, [formData, extractorFn]);

    return propertyValues;
};
